export const corruptionTypes = [
  'INVALID_CHILDREN',
  'TYPE_MISMATCH',
  'DUPLICATE_REFERENCE',
  'INVALID_CLASSES',
] as const;

export const LOCALIZATION_FEATURE_FLAGS = {
  CORRUPTION_RECOVERY: 'ff-local-1028-corruption-recovery',
  CORRUPTION_RECOVERY_ON_NEW_SITES:
    'ff-local-1030-enable-recovery-on-new-sites',
  ENABLE_RECOVERY_ON_RESTORE_WITHOUT_CORRUPTION:
    'ff-local-1344-enable-recovery-on-restore-without-corruption',
  // Gate for the localization primary-locale write guard. Roll out to 100% (guard active); dial to 0% to
  // disable the guard without a deploy if it ever rejects a legitimate save.
  LOCALIZATION_PRIMARY_GUARD: 'ff-local-2165-localization-primary-locale-guard',
} as const;

export const LOCALIZATION_LOGGER_META = {
  team: 'cms-data',
  area: 'localization',
} as const;

export const CONFIRM_UNSAVED_LOCALIZATION_SETTINGS_CHANGES_COPY = {
  title: 'Continue without saving?',
  content: 'Your changes will be lost.',
  iconType: 'warning',
  submit: {label: 'Discard changes', intent: 'danger'},
  cancel: {label: 'Cancel', intent: 'default'},
} as const;

export const CONFIRM_DELETE_LOCALIZATION_LOCALE_COPY = {
  title: 'Are you sure you want to delete?',
  content:
    'Deleting this locale will remove all localized data associated with it. This can only be undone by restoring a site backup.',
  iconType: 'warning',
  submit: {label: 'Delete locale', intent: 'danger'},
  cancel: {label: 'Cancel', intent: 'default'},
} as const;

export const CONFIRM_CHANGE_LOCALIZATION_LOCALE_CODE_COPY = {
  title: "Change this locale's ISO code?",
  content:
    "Anything you've set up for just this locale may stop applying. Nothing is deleted, and changing the code back restores it.",
  iconType: 'warning',
  submit: {label: 'Change ISO code', intent: 'primary'},
  cancel: {label: 'Cancel', intent: 'default'},
} as const;

// Note: The code to check if a language is RTL ignores the subtags, so "ar-EG" is treated as "ar".
export const RTL_LANG_CODES = new Set([
  'ar', // Arabic
  'fa', // Persian
  'he', // Hebrew
  'ur', // Urdu
  'yi', // Yiddish
]);

export const ACCOUNT_TEAM_ADD_LOCALIZE_TOOLTIP =
  'Contact your Webflow Account Team to add Webflow Localize to your Enterprise Site plan.';
export const ACCOUNT_TEAM_SUBDIRECTORY_TOOLTIP =
  'Publishing to subdirectories isn’t available on your current plan. Contact your account team to add Webflow Localize to your Enterprise Site plan.';
export const ACCOUNT_TEAM_LOCALE_LIMIT_TOOLTIP =
  'This site is at its locale limit — contact your account team to purchase additional locales or assign more to this site.';

export const getAccountTeamPublishingTooltip = ({
  canPublishLocales,
  addLocalizeTooltip,
  hasReachedLocaleLimit,
}: {
  canPublishLocales: boolean;
  addLocalizeTooltip: string;
  hasReachedLocaleLimit: boolean;
}): string =>
  canPublishLocales && hasReachedLocaleLimit
    ? ACCOUNT_TEAM_LOCALE_LIMIT_TOOLTIP
    : addLocalizeTooltip;

export const LOCALIZATION_SETTINGS_FORM_ID =
  'LocalizationSettingsPanelForm' as const;

export const LOCALIZATION_LOCALE_TYPES = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
} as const;

export const HISTORY_EVENT_NAMES = {
  added: 'locale_added',
  display_name_updated: 'locale_display_name_updated',
  tag_updated: 'locale_tag_updated',
  enabled: 'locale_enabled',
  disabled: 'locale_disabled',
  subdirectory_updated: 'locale_subdirectory_updated',
  removed: 'locale_removed',
  secondaryLocalePageContentModified: 'secondary_locale_page_content_modified',
  secondaryLocalePageSettingsModified:
    'secondary_locale_page_settings_modified',
} as const;

// Persist dismissing dialogs about creating and updating
// CMS items on localized projects.
export const CMS_ITEM_CREATION_PRIMARY_LOCALE_CONFIRM_MODAL =
  'CMS_ITEM_CREATION_PRIMARY_LOCALE_CONFIRM_MODAL';
export const CMS_ITEM_CREATION_SECONDARY_LOCALE_CONFIRM_MODAL =
  'CMS_ITEM_CREATION_SECONDARY_LOCALE_CONFIRM_MODAL';
export const CMS_ITEM_UPDATE_PRIMARY_LOCALE_CONFIRM_MODAL =
  'CMS_ITEM_UPDATE_PRIMARY_LOCALE_CONFIRM_MODAL';

export const AUTO_REDIRECT_OPT_OUT_PARAM = '?r=0';

export const LOCALIZATION_SETTINGS_PATHS = {
  Enabled: 'enabled',
  Locales: 'locales',
  PreStagedLocales: 'preStagedLocales',
  PrimaryLocales: 'primaryLocale',
  Redirect: 'redirect',
  Subdirectory: 'subdirectory',
  Tag: 'tag',
  DisplayName: 'displayName',
  DisplayImageId: 'displayImageId',
  PublishedSiteBrowserDetection: 'publishedSiteBrowserDetection',
  EnableHrefLangLinks: 'enableHreflangLinks',
  HrefPrefixForHrefLangLinks: 'hrefPrefixForHreflangLinks',
  HrefPrefixSameAsCanonicalUrl: 'hrefPrefixSameAsCanonicalUrl',
  PreventPrimaryCmsItemPropagation: 'preventPrimaryCmsItemPropagation',
} as const;

// taken from https://en.wikipedia.org/wiki/List_of_ISO_3166_country_codes
export const ISO_COUNTRIES = {
  AF: 'Afghanistan',
  AX: 'Åland Islands',
  AL: 'Albania',
  DZ: 'Algeria',
  AS: 'American Samoa',
  AD: 'Andorra',
  AO: 'Angola',
  AI: 'Anguilla',
  AQ: 'Antarctica',
  AG: 'Antigua and Barbuda',
  AR: 'Argentina',
  AM: 'Armenia',
  AW: 'Aruba',
  AU: 'Australia',
  AT: 'Austria',
  AZ: 'Azerbaijan',
  BS: 'Bahamas',
  BH: 'Bahrain',
  BD: 'Bangladesh',
  BB: 'Barbados',
  BY: 'Belarus',
  BE: 'Belgium',
  BZ: 'Belize',
  BJ: 'Benin',
  BM: 'Bermuda',
  BT: 'Bhutan',
  BO: 'Bolivia',
  BQ: 'Bonaire, Sint Eustatius and Saba',
  BA: 'Bosnia and Herzegovina',
  BW: 'Botswana',
  BV: 'Bouvet Island',
  BR: 'Brazil',
  IO: 'British Indian Ocean Territory',
  BN: 'Brunei Darussalam',
  BG: 'Bulgaria',
  BF: 'Burkina Faso',
  BI: 'Burundi',
  CV: 'Cabo Verde',
  KH: 'Cambodia',
  CM: 'Cameroon',
  CA: 'Canada',
  KY: 'Cayman Islands',
  CF: 'Central African Republic',
  TD: 'Chad',
  CL: 'Chile',
  CN: 'China',
  CX: 'Christmas Island',
  CC: 'Cocos Islands',
  CO: 'Colombia',
  KM: 'Comoros',
  CG: 'Congo',
  CD: 'DR Congo',
  CK: 'Cook Islands',
  CR: 'Costa Rica',
  CI: "Côte d'Ivoire",
  HR: 'Croatia',
  CU: 'Cuba',
  CW: 'Curaçao',
  CY: 'Cyprus',
  CZ: 'Czechia',
  DK: 'Denmark',
  DJ: 'Djibouti',
  DM: 'Dominica',
  DO: 'Dominican Republic',
  EC: 'Ecuador',
  EG: 'Egypt',
  SV: 'EL Salvador',
  GQ: 'Equatorial Guinea',
  ER: 'Eritrea',
  EE: 'Estonia',
  SZ: 'Eswatini',
  ET: 'Ethiopia',
  FK: 'Falkland Islands',
  FO: 'Faroe Islands',
  FJ: 'Fiji',
  FI: 'Finland',
  FR: 'France',
  GF: 'French Guiana',
  PF: 'French Polynesia',
  TF: 'French Southern Territories',
  GA: 'Gabon',
  GM: 'Gambia',
  GE: 'Georgia',
  DE: 'Germany',
  GH: 'Ghana',
  GI: 'Gibraltar',
  GR: 'Greece',
  GL: 'Greenland',
  GD: 'Grenada',
  GP: 'Guadeloupe',
  GU: 'Guam',
  GT: 'Guatemala',
  GG: 'Guernsey',
  GN: 'Guinea',
  GW: 'Guinea-Bissau',
  GY: 'Guyana',
  HT: 'Haiti',
  HM: 'Heard Island and McDonald Islands',
  VA: 'Holy See',
  HN: 'Honduras',
  HK: 'Hong Kong',
  HU: 'Hungary',
  IS: 'Iceland',
  IN: 'India',
  ID: 'Indonesia',
  IR: 'Iran',
  IQ: 'Iraq',
  IE: 'Ireland',
  IM: 'Isle OF Man',
  IL: 'Israel',
  IT: 'Italy',
  JM: 'Jamaica',
  JP: 'Japan',
  JE: 'Jersey',
  JO: 'Jordan',
  KZ: 'Kazakhstan',
  KE: 'Kenya',
  KI: 'Kiribati',
  KP: 'North Korea',
  KR: 'South Korea',
  KW: 'Kuwait',
  KG: 'Kyrgyzstan',
  LA: 'Laos',
  LV: 'Latvia',
  LB: 'Lebanon',
  LS: 'Lesotho',
  LR: 'Liberia',
  LY: 'Libya',
  LI: 'Liechtenstein',
  LT: 'Lithuania',
  LU: 'Luxembourg',
  MO: 'Macao',
  MG: 'Madagascar',
  MW: 'Malawi',
  MY: 'Malaysia',
  MV: 'Maldives',
  ML: 'Mali',
  MT: 'Malta',
  MH: 'Marshall Islands',
  MQ: 'Martinique',
  MR: 'Mauritania',
  MU: 'Mauritius',
  YT: 'Mayotte',
  MX: 'Mexico',
  FM: 'Micronesia',
  MD: 'Moldova',
  MC: 'Monaco',
  MN: 'Mongolia',
  ME: 'Montenegro',
  MS: 'Montserrat',
  MA: 'Morocco',
  MZ: 'Mozambique',
  MM: 'Myanmar',
  NA: 'Namibia',
  NR: 'Nauru',
  NP: 'Nepal',
  NL: 'Netherlands',
  NC: 'New Caledonia',
  NZ: 'New Zealand',
  NI: 'Nicaragua',
  NE: 'Niger',
  NG: 'Nigeria',
  NU: 'Niue',
  NF: 'Norfolk Island',
  MK: 'North Macedonia',
  MP: 'Northern Mariana Islands',
  NO: 'Norway',
  OM: 'Oman',
  PK: 'Pakistan',
  PW: 'Palau',
  PS: 'Palestine',
  PA: 'Panama',
  PG: 'Papua New Guinea',
  PY: 'Paraguay',
  PE: 'Peru',
  PH: 'Philippines',
  PN: 'Pitcairn',
  PL: 'Poland',
  PT: 'Portugal',
  PR: 'Puerto Rico',
  QA: 'Qatar',
  RE: 'Réunion',
  RO: 'Romania',
  RU: 'Russian Federation',
  RW: 'Rwanda',
  BL: 'Saint Barthélemy',
  SH: 'Saint Helena, Ascension and Tristan DA Cunha',
  KN: 'Saint Kitts and Nevis',
  LC: 'Saint Lucia',
  MF: 'Saint Martin',
  PM: 'Saint Pierre and Miquelon',
  VC: 'Saint Vincent and the Grenadines',
  WS: 'Samoa',
  SM: 'San Marino',
  ST: 'Sao Tome and Principe',
  SA: 'Saudi Arabia',
  SN: 'Senegal',
  RS: 'Serbia',
  SC: 'Seychelles',
  SL: 'Sierra Leone',
  SG: 'Singapore',
  SX: 'Sint Maarten',
  SK: 'Slovakia',
  SI: 'Slovenia',
  SB: 'Solomon Islands',
  SO: 'Somalia',
  ZA: 'South Africa',
  GS: 'South Georgia and the South Sandwich Islands',
  SS: 'South Sudan',
  ES: 'Spain',
  LK: 'Sri Lanka',
  SD: 'Sudan',
  SR: 'Suriname',
  SJ: 'Svalbard and Jan Mayen',
  SE: 'Sweden',
  CH: 'Switzerland',
  SY: 'Syria',
  TW: 'Taiwan',
  TJ: 'Tajikistan',
  TZ: 'Tanzania',
  TH: 'Thailand',
  TL: 'Timor-Leste',
  TG: 'Togo',
  TK: 'Tokelau',
  TO: 'Tonga',
  TT: 'Trinidad and Tobago',
  TN: 'Tunisia',
  TR: 'Türkiye',
  TM: 'Turkmenistan',
  TC: 'Turks and Caicos Islands',
  TV: 'Tuvalu',
  UG: 'Uganda',
  UA: 'Ukraine',
  AE: 'United Arab Emirates',
  GB: 'United Kingdom',
  US: 'United States',
  UY: 'Uruguay',
  UZ: 'Uzbekistan',
  VU: 'Vanuatu',
  VE: 'Venezuela',
  VN: 'Viet Nam',
  VG: 'British Virgin Islands',
  VI: 'Virgin Islands',
  WF: 'Wallis and Futuna',
  EH: 'Western Sahara',
  YE: 'Yemen',
  ZM: 'Zambia',
  ZW: 'Zimbabwe',
};

// taken from https://en.wikipedia.org/wiki/List_of_ISO_639_language_codes
export const ISO_LANGUAGES = {
  ab: 'Abkhazian',
  aa: 'Afar',
  af: 'Afrikaans',
  ak: 'Akan',
  sq: 'Albanian',
  am: 'Amharic',
  ar: 'Arabic',
  an: 'Aragonese',
  hy: 'Armenian',
  as: 'Assamese',
  av: 'Avaric',
  ay: 'Aymara',
  az: 'Azerbaijani',
  bm: 'Bambara',
  ba: 'Bashkir',
  eu: 'Basque',
  be: 'Belarusian',
  bn: 'Bengali',
  bi: 'Bislama',
  bs: 'Bosnian',
  br: 'Breton',
  bg: 'Bulgarian',
  my: 'Burmese',
  ca: 'Catalan',
  ch: 'Chamorro',
  ce: 'Chechen',
  ny: 'Chichewa',
  zh: 'Chinese',
  cv: 'Chuvash',
  kw: 'Cornish',
  co: 'Corsican',
  cr: 'Cree',
  hr: 'Croatian',
  cs: 'Czech',
  da: 'Danish',
  dv: 'Divehi',
  nl: 'Dutch',
  dz: 'Dzongkha',
  en: 'English',
  eo: 'Esperanto',
  et: 'Estonian',
  ee: 'Ewe',
  fo: 'Faroese',
  fj: 'Fijian',
  fi: 'Finnish',
  fr: 'French',
  fy: 'Western Frisian',
  ff: 'Fulah',
  gd: 'Gaelic',
  gl: 'Galician',
  lg: 'Ganda',
  ka: 'Georgian',
  de: 'German',
  el: 'Greek',
  kl: 'Kalaallisut',
  gn: 'Guarani',
  gu: 'Gujarati',
  ht: 'Haitian',
  ha: 'Hausa',
  he: 'Hebrew',
  hz: 'Herero',
  hi: 'Hindi',
  ho: 'Hiri Motu',
  hu: 'Hungarian',
  is: 'Icelandic',
  io: 'Ido',
  ig: 'Igbo',
  id: 'Indonesian',
  ia: 'Interlingua',
  ie: 'Interlingue',
  iu: 'Inuktitut',
  ik: 'Inupiaq',
  ga: 'Irish',
  it: 'Italian',
  ja: 'Japanese',
  jv: 'Javanese',
  kn: 'Kannada',
  kr: 'Kanuri',
  ks: 'Kashmiri',
  kk: 'Kazakh',
  km: 'Central Khmer',
  ki: 'Kikuyu',
  rw: 'Kinyarwanda',
  ky: 'Kyrgyz',
  kv: 'Komi',
  kg: 'Kongo',
  ko: 'Korean',
  kj: 'Kuanyama',
  ku: 'Kurdish',
  lo: 'Lao',
  lv: 'Latvian',
  li: 'Limburgan',
  ln: 'Lingala',
  lt: 'Lithuanian',
  lu: 'Luba-Katanga',
  lb: 'Luxembourgish',
  mk: 'Macedonian',
  mg: 'Malagasy',
  ms: 'Malay',
  ml: 'Malayalam',
  mt: 'Maltese',
  gv: 'Manx',
  mi: 'Maori',
  mr: 'Marathi',
  mh: 'Marshallese',
  mn: 'Mongolian',
  na: 'Nauru',
  nv: 'Navajo',
  nd: 'North Ndebele',
  nr: 'South Ndebele',
  ng: 'Ndonga',
  ne: 'Nepali',
  no: 'Norwegian',
  nb: 'Norwegian Bokmål',
  nn: 'Norwegian Nynorsk',
  oc: 'Occitan',
  oj: 'Ojibwa',
  or: 'Oriya',
  om: 'Oromo',
  os: 'Ossetian',
  ps: 'Pashto',
  fa: 'Persian',
  pl: 'Polish',
  pt: 'Portuguese',
  pa: 'Punjabi',
  qu: 'Quechua',
  ro: 'Romanian',
  rm: 'Romansh',
  rn: 'Rundi',
  ru: 'Russian',
  se: 'Northern Sami',
  sma: 'Southern Sami',
  smj: 'Lule Sami',
  sm: 'Samoan',
  sg: 'Sango',
  sc: 'Sardinian',
  sr: 'Serbian',
  sn: 'Shona',
  sd: 'Sindhi',
  si: 'Sinhala',
  sk: 'Slovak',
  sl: 'Slovenian',
  so: 'Somali',
  st: 'Southern Sotho',
  es: 'Spanish',
  su: 'Sundanese',
  sw: 'Swahili',
  ss: 'Swati',
  sv: 'Swedish',
  tl: 'Tagalog',
  ty: 'Tahitian',
  tg: 'Tajik',
  ta: 'Tamil',
  tt: 'Tatar',
  te: 'Telugu',
  th: 'Thai',
  bo: 'Tibetan',
  ti: 'Tigrinya',
  to: 'Tonga',
  ts: 'Tsonga',
  tn: 'Tswana',
  tr: 'Turkish',
  tk: 'Turkmen',
  tw: 'Twi',
  ug: 'Uighur',
  uk: 'Ukrainian',
  ur: 'Urdu',
  uz: 'Uzbek',
  ve: 'Venda',
  vi: 'Vietnamese',
  vo: 'Volapük',
  wa: 'Walloon',
  cy: 'Welsh',
  wo: 'Wolof',
  xh: 'Xhosa',
  ii: 'Sichuan Yi',
  yi: 'Yiddish',
  yo: 'Yoruba',
  za: 'Zhuang',
  zu: 'Zulu',
};

const LOCALE_NAME_REGEX = /^(?<lang>[^(]+)(\s\((?<country>[^)]+)\))?$/;

const LANG_CODES_BY_NAME = new Map(
  Object.entries(ISO_LANGUAGES).map(([code, name]) => [name, code])
);
const COUNTRY_CODES_BY_NAME = new Map(
  Object.entries(ISO_COUNTRIES).map(([code, name]) => [name, code])
);

/**
 * Returns the locale tag for a locale's display name, such as `fr-CA` for
 * "French (Canada)", or `null` when the language isn't an ISO 639 name.
 */
export const getLocaleTagFromName = (
  maybeLocaleName: string
): string | null => {
  const {lang = '', country = ''} =
    LOCALE_NAME_REGEX.exec(maybeLocaleName)?.groups ?? {};
  const isoLang = LANG_CODES_BY_NAME.get(lang);
  if (!isoLang) {
    return null;
  }
  const isoCountry = COUNTRY_CODES_BY_NAME.get(country);
  return isoCountry ? `${isoLang}-${isoCountry}` : isoLang;
};
